import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Button, Space, Select, DatePicker, message, Progress, Modal, Form, Input, InputNumber, Radio, Popconfirm, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import AttachmentUpload from './AttachmentUpload';
import api from '../utils/axios';

const { Option } = Select;

const statusColor = (status) => {
  switch (status) {
    case 'completed': return 'green';
    case 'in_progress': return 'blue';
    case 'abnormal': return 'red';
    case 'pending':
    default: return 'default';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'completed': return '已完成';
    case 'in_progress': return '进行中';
    case 'abnormal': return '异常结束';
    case 'pending': return '待开始';
    default: return '待开始';
  }
};

const EmployeeTasks = ({ employee }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [progress, setProgress] = useState(0);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [startDateType, setStartDateType] = useState('hire_date'); // 'hire_date' 或 'current_date'
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [logModal, setLogModal] = useState({ visible: false, task: null });
  const [logForm] = Form.useForm();
  const [logs, setLogs] = useState([]);

  const loadTasks = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/employee-tasks/employees/${employee.id}/tasks`);
      if (res.data?.success) {
        setTasks(res.data.data.tasks || []);
        setProgress(res.data.data.progress || 0);
      }
    } catch (e) {
      message.error('加载任务失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTasks(); }, [employee?.id]);

  const getSourceText = (source) => {
    switch (source) {
      case 'template': return '自动生成';
      case 'manual': return '手动添加';
      default: return '未知';
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'template': return 'blue';
      case 'manual': return 'green';
      default: return 'default';
    }
  };

  const columns = [
    { 
      title: '名称', 
      dataIndex: 'name', 
      key: 'name',
      render: (name, record) => {
        const tooltipContent = (
          <div>
            {record.description && (
              <div style={{ marginBottom: 8 }}>
                <strong>任务描述：</strong><br />
                {record.description}
              </div>
            )}
            {record.created_at && (
              <div style={{ marginBottom: 4, fontSize: '12px', color: '#999' }}>
                <strong>创建时间：</strong>{new Date(record.created_at).toLocaleString('zh-CN')}
              </div>
            )}
            {(record.created_by_name || record.created_by_username) && (
              <div style={{ fontSize: '12px', color: '#999' }}>
                <strong>创建人：</strong>{record.created_by_name || record.created_by_username || '未知'}
              </div>
            )}
          </div>
        );

        const isCompleted = record.status === 'completed';
        const nameStyle = isCompleted ? {} : { color: 'red', fontWeight: 'bold' };

        return (
          record.description || record.created_at || record.created_by_name ? (
            <Tooltip title={tooltipContent} placement="topLeft">
              <span style={nameStyle}>{name}</span>
            </Tooltip>
          ) : (
            <span style={nameStyle}>{name}</span>
          )
        );
      }
    },
    { 
      title: '来源', 
      dataIndex: 'source', 
      key: 'source', 
      width: 100, 
      render: (source, record) => (
        <div>
          <Tag color={getSourceColor(source)} size="small">
            {getSourceText(source)}
          </Tag>
          {source === 'manual' && record.created_by_name && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.created_by_name || record.created_by_username || '未知用户'}
            </div>
          )}
          {source === 'manual' && record.created_at && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              {new Date(record.created_at).toLocaleDateString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit' 
              })}
            </div>
          )}
        </div>
      )
    },
    { title: '窗口', key: 'window', width: 200, render: (_, r) => (
      r.window_start_date || r.window_end_date ? (
        <span>{r.window_start_date || '-'} ~ {r.window_end_date || '-'}</span>
      ) : '-'
    )},
    { title: '截止', dataIndex: 'due_date', key: 'due_date', width: 140 },
    { title: '目标', key: 'target', width: 140, render: (_, r) => (
      r.target_value ? <span>{r.current_value || 0} / {r.target_value}</span> : '-'
    )},
    { title: '状态', dataIndex: 'status', key: 'status', width: 120, render: (s) => <Tag color={statusColor(s)}>{getStatusText(s)}</Tag> },
    {
      title: '操作', key: 'actions', width: 80, fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.status !== 'completed' && (
            <Button size="small" type="link" onClick={() => handleComplete(record)}>完成</Button>
          )}
          {(record.description || record.created_at || record.created_by_name) && (
            <Tooltip title={
              <div>
                {record.description && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>任务描述：</strong><br />
                    {record.description}
                  </div>
                )}
                {record.created_at && (
                  <div style={{ marginBottom: 4, fontSize: '12px', color: '#999' }}>
                    <strong>创建时间：</strong>{new Date(record.created_at).toLocaleString('zh-CN')}
                  </div>
                )}
                {(record.created_by_name || record.created_by_username) && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    <strong>创建人：</strong>{record.created_by_name || record.created_by_username || '未知'}
                  </div>
                )}
              </div>
            } placement="topLeft">
              <Button size="small" type="link">详情</Button>
            </Tooltip>
          )}
          {hasPermission('employee_task.delete') && (
            <Popconfirm
              title="确定要删除这个任务吗？"
              description="删除后无法恢复，请谨慎操作。"
              onConfirm={() => handleDelete(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                size="small" 
                type="link" 
                danger
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const openInstantiate = async () => {
    setTemplateModalVisible(true);
    setSelectedTemplateIds([]); // 重置选择
    setStartDateType('hire_date'); // 重置时间类型
    try {
      const res = await api.get('/employee-tasks/templates', {
        params: { department_id: employee?.department_id, position_id: employee?.position_id }
      });
      if (res.data?.success) setTemplates(res.data.data || []);
    } catch (e) { /* 忽略 */ }
  };

  const handleInstantiate = async () => {
    if (!selectedTemplateIds.length) return message.warning('请选择模板');
    try {
      // 根据选择的时间类型确定开始日期
      let startDate = null;
      if (startDateType === 'hire_date' && employee?.hire_date) {
        startDate = employee.hire_date;
      } else if (startDateType === 'current_date') {
        startDate = new Date().toISOString().split('T')[0]; // 当前日期 YYYY-MM-DD
      }
      
      await api.post(`/employee-tasks/employees/${employee.id}/tasks/instantiate`, {
        template_ids: selectedTemplateIds,
        start_date: startDate
      });
      message.success('任务已生成');
      setTemplateModalVisible(false);
      setSelectedTemplateIds([]);
      loadTasks();
    } catch (e) {
      message.error('生成任务失败');
    }
  };

  // 完成任务相关状态
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [completeForm] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleComplete = (task) => {
    setCurrentTask(task);
    setCompleteModalVisible(true);
    completeForm.resetFields();
    setAttachments([]);
  };

  const handleCompleteSubmit = async (values, action) => {
    if (!currentTask) {
      console.error('当前任务为空');
      message.error('任务信息错误，请刷新页面重试');
      return;
    }
    
    setSubmitting(true);
    try {
      const requestData = {
        status: action === 'save' ? 'in_progress' : 'completed',
        completion_notes: values.completion_notes || ''
      };
      
      if (action === 'completed') {
        // 转换为MySQL兼容的日期时间格式
        const now = new Date();
        requestData.completed_at = now.getFullYear() + '-' + 
          String(now.getMonth() + 1).padStart(2, '0') + '-' + 
          String(now.getDate()).padStart(2, '0') + ' ' + 
          String(now.getHours()).padStart(2, '0') + ':' + 
          String(now.getMinutes()).padStart(2, '0') + ':' + 
          String(now.getSeconds()).padStart(2, '0');
      }
      
      // 添加附件ID
      if (attachments && attachments.length > 0) {
        const attachmentIds = attachments.map(file => file.fileData?.id || file.uid).filter(Boolean);
        requestData.attachment_ids = attachmentIds;
      }

      console.log('提交任务数据:', {
        taskId: currentTask.id,
        employeeId: currentTask.employee_id,
        action,
        requestData
      });

      await api.patch(`/employee-tasks/employees/${employee.id}/tasks/${currentTask.id}`, requestData);
      
      message.success(action === 'save' ? '任务已暂存' : '任务已完成');
      setCompleteModalVisible(false);
      setCurrentTask(null);
      loadTasks();
    } catch (e) {
      console.error('任务操作失败:', e);
      console.error('错误详情:', e.response?.data);
      
      // 更详细的错误处理
      let errorMessage = action === 'save' ? '暂存任务失败' : '完成任务失败';
      
      if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.message) {
        errorMessage = e.message;
      } else if (typeof e === 'string') {
        errorMessage = e;
      } else {
        errorMessage = '操作失败，请稍后重试';
      }
      
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteCancel = () => {
    setCompleteModalVisible(false);
    setCurrentTask(null);
    completeForm.resetFields();
    setAttachments([]);
  };

  const handleDelete = async (task) => {
    try {
      await api.delete(`/employee-tasks/employees/${employee.id}/tasks/${task.id}`);
      message.success('任务删除成功');
      loadTasks();
    } catch (e) {
      message.error(e.response?.data?.message || '删除任务失败');
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post(`/employee-tasks/employees/${employee.id}/tasks`, values);
      message.success('已创建任务');
      setCreateModalVisible(false);
      form.resetFields();
      loadTasks();
    } catch (e) {
      message.error('创建任务失败');
    }
  };

  const openLogModal = async (task) => {
    setLogModal({ visible: true, task });
    logForm.resetFields();
    try {
      const res = await api.get(`/employee-tasks/employees/${employee.id}/tasks/${task.id}/logs`);
      if (res.data?.success) setLogs(res.data.data || []);
    } catch (e) { /* 忽略 */ }
  };

  const submitLog = async () => {
    try {
      const values = await logForm.validateFields();
      await api.post(`/employee-tasks/employees/${employee.id}/tasks/${logModal.task.id}/logs`, values);
      message.success('已记录');
      // 刷新日志与任务
      const res = await api.get(`/employee-tasks/employees/${employee.id}/tasks/${logModal.task.id}/logs`);
      if (res.data?.success) setLogs(res.data.data || []);
      loadTasks();
      logForm.resetFields();
    } catch (e) {
      message.error('记录失败');
    }
  };

  const handleGenerateByDepartment = async () => {
    try {
      console.log('🔍 开始生成任务，员工信息:', {
        id: employee.id,
        name: employee.name,
        department_id: employee.department_id,
        department_name: employee.department_name,
        position_id: employee.position_id,
        position_name: employee.position_name,
        hire_date: employee.hire_date
      });
      
      const res = await api.post(`/employees/${employee.id}/generate-tasks`, {
        department_id: employee.department_id || null,
        position_id: employee.position_id || null,
        hire_date: employee.hire_date,
        department_name: employee.department_name || null,
        position_name: employee.position_name || null
      });
      
      console.log('🔍 任务生成API响应:', res.data);
      
      if (res.data?.success) {
        message.success(res.data.message || '任务生成成功');
        console.log('✅ 任务生成结果:', res.data.data);
      } else {
        message.error('生成任务失败');
        console.error('❌ 任务生成失败:', res.data);
      }
      loadTasks();
    } catch (e) {
      console.error('❌ 任务生成请求失败:', e);
      message.error(e.response?.data?.message || '生成任务失败');
    }
  };

  // 自动检查并生成任务
  const autoGenerateTasksIfNeeded = async () => {
    if (!employee?.id || !employee?.department_id || !employee?.position_id) {
      return;
    }

    try {
      // 检查是否需要自动生成任务
      const res = await api.get(`/employees/${employee.id}/check-missing-tasks`);
      if (res.data?.success && res.data.data?.missingCount > 0) {
        console.log(`发现${res.data.data.missingCount}个缺失的任务模板，自动生成中...`);
        
        // 静默生成任务，不显示成功消息（避免干扰用户）
        try {
          const generateRes = await api.post(`/employees/${employee.id}/generate-tasks`, {
            department_id: employee.department_id || null,
            position_id: employee.position_id || null,
            hire_date: employee.hire_date,
            department_name: employee.department_name || null,
            position_name: employee.position_name || null
          });
          
          if (generateRes.data?.success && generateRes.data.data?.created > 0) {
            console.log(`自动生成了${generateRes.data.data.created}个新任务`);
            loadTasks(); // 刷新任务列表
          }
        } catch (generateError) {
          console.warn('自动生成任务失败:', generateError);
        }
      }
    } catch (e) {
      console.warn('自动检查任务失败:', e);
    }
  };

  // 当员工信息加载完成后，自动检查并生成任务
  useEffect(() => {
    if (employee?.id) {
      // 延迟执行，确保任务列表先加载
      setTimeout(() => {
        autoGenerateTasksIfNeeded();
      }, 1000);
    }
  }, [employee?.id, employee?.department_id, employee?.position_id]);

  return (
    <Card 
      title={
        <Space size={12}>
          <span>入职后任务</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#666' }}>总体进度</span>
            <div style={{ width: 180 }}><Progress percent={progress} size="small" /></div>
          </div>
        </Space>
      }
      extra={
        <Space size={12}>
          <Button size="small" onClick={() => setCreateModalVisible(true)}>新建任务</Button>
          <Button size="small" type="primary" onClick={openInstantiate}>按模板生成</Button>
          <Button size="small" type="default" onClick={handleGenerateByDepartment}>
            按部门生成
          </Button>
        </Space>
      }
    >
      <Table rowKey="id" loading={loading} dataSource={tasks} columns={columns} pagination={false} size="small" scroll={{ x: 'max-content' }} />

      <Modal
        title="按模板生成任务"
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        onOk={handleInstantiate}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 6 }}>选择模板：</div>
            <Select mode="multiple" value={selectedTemplateIds} onChange={setSelectedTemplateIds} style={{ width: '100%' }} placeholder="请选择任务模板">
              {templates.map(t => (<Option key={t.id} value={t.id}>{t.name}（{t.section}/{t.type}）</Option>))}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 6 }}>开始时间（用于计算截止日期）：</div>
            <Radio.Group value={startDateType} onChange={(e) => setStartDateType(e.target.value)}>
              <Radio value="hire_date">以入职时间为起始</Radio>
              <Radio value="current_date">以当前时间为起始</Radio>
            </Radio.Group>
            {startDateType === 'hire_date' && employee?.hire_date && (
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                入职时间：{employee.hire_date}
              </div>
            )}
            {startDateType === 'current_date' && (
              <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                当前时间：{new Date().toLocaleDateString('zh-CN', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                })}
              </div>
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        title="新建任务"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        onOk={handleCreate}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="section" label="分块" rules={[{ required: true, message: '请输入分块' }]}>
            <Input placeholder="如：学习与考试/沟通回访/到店带教" />
          </Form.Item>
          <Form.Item name="type" label="类型" rules={[{ required: true, message: '请输入类型' }]}>
            <Input placeholder="course/call/visit/document" />
          </Form.Item>
          <Form.Item name="description" label="任务描述">
            <Input.TextArea rows={3} placeholder="请输入任务的详细描述、话术或操作指南" />
          </Form.Item>
          <Form.Item name="due_date" label="截止日期">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="target_value" label="目标值（如邀约人数）">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如：2" />
          </Form.Item>
          <Form.Item name="window_days" label="窗口期天数（如7天）">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如：7" />
          </Form.Item>
          <Form.Item name="window_offset_days" label="窗口偏移天数（相对开始日）">
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="如：0" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="记录任务进度"
        open={logModal.visible}
        onCancel={() => setLogModal({ visible: false, task: null })}
        onOk={submitLog}
      >
        <Form form={logForm} layout="vertical">
          <Form.Item name="value" label="数量" rules={[{ required: true, message: '请输入数量' }]}> 
            <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="如：1" />
          </Form.Item>
          <Form.Item name="note" label="备注"> 
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 6, color: '#666' }}>历史记录</div>
          <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
            {logs.length === 0 ? (
              <div style={{ color: '#999' }}>暂无记录</div>
            ) : logs.map(l => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #eee' }}>
                <div>+{l.value} <span style={{ color: '#999' }}>{l.note || ''}</span></div>
                <div style={{ color: '#999' }}>{l.log_at?.replace('T',' ').split('.')[0]}</div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* 完成任务模态框 */}
      <Modal
        title={`完成任务 - ${currentTask?.name || ''}`}
        open={completeModalVisible}
        onCancel={handleCompleteCancel}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleCompleteCancel}>
            取消
          </Button>,
          <Button 
            key="save" 
            type="default" 
            loading={submitting}
            onClick={() => {
              completeForm.validateFields()
                .then(values => {
                  handleCompleteSubmit(values, 'save');
                })
                .catch(errorInfo => {
                  console.error('表单验证失败:', errorInfo);
                  message.error('请检查表单填写是否正确');
                });
            }}
          >
            保存
          </Button>,
          <Button 
            key="complete" 
            type="primary" 
            loading={submitting}
            onClick={() => {
              completeForm.validateFields()
                .then(values => {
                  handleCompleteSubmit(values, 'completed');
                })
                .catch(errorInfo => {
                  console.error('表单验证失败:', errorInfo);
                  message.error('请检查表单填写是否正确');
                });
            }}
          >
            完成
          </Button>,
        ]}
      >
        <Form
          form={completeForm}
          layout="vertical"
          onFinish={(values) => {
            // 默认保存操作
            handleCompleteSubmit(values, 'save');
          }}
        >
          <Form.Item
            name="completion_notes"
            label="完成情况描述"
            rules={[{ required: true, message: '请填写完成情况描述' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="请详细描述任务的完成情况..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="attachments"
            label="相关附件"
            extra="支持图片、PDF、Word、Excel等格式，单个文件不超过10MB"
          >
            <AttachmentUpload
              value={attachments}
              onChange={setAttachments}
              employeeId={currentTask?.employee_id}
              compact={false}
              showQrButton={true}
              hideList={false}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EmployeeTasks;


